import React, { useEffect, useState, createElement } from 'react';
import { Hero } from './components/Hero';
import { HookSection } from './components/HookSection';
import { ProblemSection } from './components/ProblemSection';
import { MechanismSection } from './components/MechanismSection';
import { WhatsInsideSection } from './components/WhatsInsideSection';
import { WhoIsThisFor } from './components/WhoIsThisFor';
import { BonusSection } from './components/BonusSection';
import { CredibilitySection } from './components/CredibilitySection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { FAQSection } from './components/FAQSection';
import { PriceSection } from './components/PriceSection';
import { RiskReversalSection } from './components/RiskReversalSection';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
import { ScrollCTA } from './components/ScrollCTA';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
type Page = 'home' | 'terms' | 'privacy';
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
export function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  useEffect(() => {
    // --- Favicon emoji ---
    const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.rel = 'icon';
    favicon.href = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">🏚️</text></svg>';
    document.head.appendChild(favicon);
    // --- Page title ---
    document.title = '🔒 The Concrete Bunker Playbook — $6.36';
    const setMeta = (attrs: Record<string, string>) => {
      const key = attrs.property || attrs.name;
      const selector = attrs.property ? `meta[property="${attrs.property}"]` : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    setMeta({
      name: 'description',
      content: 'Build an invisible, undetectable gun safe inside your walls for under $500. Step-by-step blueprint used by contractors. Instant digital download.'
    });
    setMeta({
      property: 'og:type',
      content: 'website'
    });
    setMeta({
      property: 'og:title',
      content: 'The Concrete Bunker Playbook — Stop Being a Target'
    });
    setMeta({
      property: 'og:description',
      content: 'Build an invisible gun safe inside your walls for under $500. No permits. No paper trail. Instant digital download — $6.36.'
    });
    setMeta({
      property: 'og:image',
      content: 'https://www.betterbysteps.com/cdn/shop/files/blueprint-og.jpg'
    });
    setMeta({
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    setMeta({
      name: 'twitter:title',
      content: 'The Concrete Bunker Playbook — $6.36'
    });
    setMeta({
      name: 'twitter:description',
      content: 'Build an invisible gun safe inside your walls for under $500. No permits. No paper trail.'
    });
    // --- Meta Pixel ---
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
      return;
    }
    const n: any = function (...args: any[]) {
      n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    window.fbq = n;
    window._fbq = n;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', '1917228908920166');
    window.fbq('track', 'PageView');
  }, []);
  const handleNavigate = (page: 'terms' | 'privacy') => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleBack = () => {
    setCurrentPage('home');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (currentPage === 'terms') {
    return <TermsPage onBack={handleBack} data-id="element-0" />;
  }
  if (currentPage === 'privacy') {
    return <PrivacyPage onBack={handleBack} data-id="element-1" />;
  }
  return <div className="min-h-screen w-full bg-dark-bg font-inter antialiased" data-id="element-2">
      {/* Hook & Problem */}
      <Hero data-id="element-3" />
      <HookSection data-id="element-4" />
      <ProblemSection data-id="element-5" />

      {/* Solution & Value */}
      <MechanismSection data-id="element-6" />
      <WhatsInsideSection data-id="element-7" />

      {/* CTA #1 — Post value reveal: reader knows exactly what they get */}
      <ScrollCTA label="GET THE BLUEPRINT — $6.36" subtext="You now know exactly what's inside. One weekend. $500 in materials. A vault nobody can find." data-id="element-8" />

      {/* Trust & Proof */}
      <WhoIsThisFor data-id="element-9" />
      <CredibilitySection data-id="element-10" />
      <TestimonialsSection data-id="element-11" />

      {/* CTA #2 — Post social proof: trust is at its peak */}
      <ScrollCTA label="I'M READY — GET ACCESS NOW" subtext="Join thousands of gun owners who chose invisibility over a neon sign. 60-day guarantee, no questions asked." data-id="element-12" />

      {/* Objection Handling */}
      <FAQSection data-id="element-13" />

      {/* CTA #3 — Post FAQ: last objection removed, decision time */}
      <ScrollCTA label="SECURE YOUR COPY — $6.36" subtext="Every question answered. Every objection resolved. The only thing left is to act." data-id="element-14" />

      {/* Close — Bonuses shown immediately before price so buyer sees full order value */}
      <BonusSection data-id="element-15" />
      <PriceSection data-id="element-16" />
      <RiskReversalSection data-id="element-17" />
      <FinalCTA data-id="element-18" />

      {/* Footer */}
      <Footer onNavigate={handleNavigate} data-id="element-19" />
    </div>;
}